import { Fragment } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ApiBlueprint } from '@backstage/frontend-plugin-api';
import {
  swappableComponentsApiRef,
  PageLayout,
  type PageLayoutProps,
  type SwappableComponentsApi,
} from '@backstage/frontend-plugin-api';
import { Page, Header } from '@backstage/core-components';
import { NavLink } from 'react-router-dom';
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  alertApiRef,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  githubAuthApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';
import {
  toastApiRef,
  pluginHeaderActionsApiRef,
} from '@backstage/frontend-plugin-api';
import { GithubAuth, OAuth2 } from '@backstage/core-app-api';
import { oidcAuthApiRef } from '../apis';

/** Custom page layout that matches the Home page header style using Page + Header from @backstage/core-components */
function CustomPageLayout({
  title,
  headerActions,
  tabs,
  noHeader,
  children,
}: PageLayoutProps) {
  if (noHeader) {
    return <>{children}</>;
  }
  return (
    <Page themeId="tool">
      <Header title={title ?? ''}>
        {headerActions
          ?.filter(Boolean)
          .map((action, i) => <Fragment key={i}>{action}</Fragment>)}
      </Header>
      {tabs && tabs.length > 0 && (
        <div style={{ gridArea: 'pageSubheader' }}>
          <nav
            style={{
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#fff',
              padding: '0 24px',
              display: 'flex',
            }}
          >
            {tabs.map(tab => (
              <NavLink
                key={tab.id}
                to={tab.href ?? '#'}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '8px 16px',
                  textDecoration: 'none',
                  color: isActive ? '#1f6fce' : '#666',
                  fontSize: 14,
                  fontWeight: 500,
                  borderBottom: isActive ? '2px solid #1f6fce' : '2px solid transparent',
                })}
              >
                {tab.icon}
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
      <div style={{ gridArea: 'pageContent', minWidth: 0 }}>
        {children}
      </div>
    </Page>
  );
}

export const apiModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'swappable-components',
      params: define =>
        define(
          createApiFactory({
            api: swappableComponentsApiRef,
            deps: {},
            factory: (): SwappableComponentsApi => ({
              getComponent(ref) {
                if ((ref as any).id === PageLayout.ref.id) {
                  return CustomPageLayout as (props: typeof ref extends { $$type: string } ? any : never) => JSX.Element | null;
                }
                // Fall back to the built-in default for other swappable components
                return (ref as any).defaultComponent;
              },
            }),
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'scm-integrations',
      params: define =>
        define(
          createApiFactory({
            api: scmIntegrationsApiRef,
            deps: { configApi: configApiRef },
            factory: ({ configApi }) =>
              ScmIntegrationsApi.fromConfig(configApi),
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'scm-auth',
      params: define => define(ScmAuth.createDefaultApiFactory()),
    }),
    ApiBlueprint.make({
      name: 'toast',
      params: define =>
        define(
          createApiFactory({
            api: toastApiRef,
            deps: { alertApi: alertApiRef },
            factory: ({ alertApi }) => ({
              post(toast) {
                alertApi.post({
                  message:
                    typeof toast.title === 'string'
                      ? toast.title
                      : toast.description &&
                        typeof toast.description === 'string'
                      ? toast.description
                      : 'Notification',
                  severity:
                    toast.status === 'danger'
                      ? 'error'
                      : toast.status === 'neutral'
                      ? 'info'
                      : toast.status,
                  display: toast.timeout ? 'transient' : 'permanent',
                });

                return {
                  close() {
                    // AlertApi does not support dismissing individual alerts.
                  },
                };
              },
            }),
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'plugin-header-actions',
      params: define =>
        define(
          createApiFactory({
            api: pluginHeaderActionsApiRef,
            deps: {},
            factory: () => ({
              getPluginHeaderActions: () => [],
            }),
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'github-auth',
      params: define =>
        define(
          createApiFactory({
            api: githubAuthApiRef,
            deps: {
              discoveryApi: discoveryApiRef,
              oauthRequestApi: oauthRequestApiRef,
              configApi: configApiRef,
            },
            factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
              GithubAuth.create({
                configApi,
                discoveryApi,
                oauthRequestApi,
                defaultScopes: ['repo', 'user:email', 'read:org', 'workflow'],
              }),
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'oidc-auth',
      params: define =>
        define(
          createApiFactory({
            api: oidcAuthApiRef,
            deps: {
              discoveryApi: discoveryApiRef,
              oauthRequestApi: oauthRequestApiRef,
              configApi: configApiRef,
            },
            factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
              OAuth2.create({
                configApi,
                discoveryApi,
                oauthRequestApi,
                provider: {
                  id: 'oidc',
                  title: 'CIID',
                  icon: () => null,
                },
                environment: configApi.getOptionalString('auth.environment'),
                defaultScopes: ['openid', 'profile', 'email'],
                popupOptions: {
                  size: {
                    width: 770,
                    height: 950,
                  },
                },
              }),
          }),
        ),
    }),
  ],
});
